import { useState } from "react";
import { Link } from "react-router-dom";
import { useClientId } from "@/shared/hooks/useClientId";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useDocumentTitle, buildPageTitle } from "@/shared/hooks/useDocumentTitle";
import { ClientPropertiesSection } from "@/features/properties/components/ClientPropertiesSection";
import { ComprehensiveAddPropertyModal } from "@/features/properties/components/ComprehensiveAddPropertyModal";
import { PropertyImportModal } from "@/features/properties/components/projects/PropertyImportModal";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/shared/ui/breadcrumb";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";
import { Building2 } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { useIsDemoUser } from "@/shared/hooks/useIsDemoUser";
import { useClientData } from "@/features/projects/contexts/ClientDataContext";

export default function ClientProperties() {
  const { clientId } = useClientId();
  const { projects, loading } = useProjects();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const { toast } = useToast();
  const { refetch } = useClientData();
  const isDemoUser = useIsDemoUser();

  const client = projects.find(p => p.id === clientId);

  useDocumentTitle(client ? buildPageTitle([client.name, "Properties"]) : null);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="rounded-2xl border border-border/50 bg-card/50 p-6">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-52" />
                <Skeleton className="h-4 w-80" />
              </div>
            </div>
            <div className="flex gap-3">
              <Skeleton className="h-9 w-24" />
              <Skeleton className="h-9 w-28" />
            </div>
          </div>
          <Skeleton className="h-11 w-full" />
        </div>
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[82px] w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Building2 className="h-16 w-16 text-muted-foreground mx-auto" />
          <h2 className="text-3xl font-bold">Client not found</h2>
          <p className="text-muted-foreground">The requested client could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen min-h-0 flex-col overflow-hidden">
      {/* Header with breadcrumbs and Contact Sales */}
      <div className="flex items-center justify-between border-b border-border bg-card/50 backdrop-blur-sm px-6 py-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">{client.name}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>Properties</BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        {isDemoUser && (
          <Button variant="default" size="sm" asChild>
            <a href="https://www.roboticimaging.com/contact" target="_blank" rel="noopener noreferrer">
              Contact Sales
            </a>
          </Button>
        )}
      </div>

      {/* Full Properties Section */}
      <div className="min-h-0 flex-1 overflow-hidden p-6">
      <ClientPropertiesSection
        clientId={clientId!}
        projects={projects}
        onAddProperty={() => setShowAddModal(true)}
        onImportCSV={() => setShowImportModal(true)}
      />

      {/* MVP2-282: Add Property Modal */}
      <ComprehensiveAddPropertyModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        clientId={clientId!}
        onPropertyAdded={() => {
          refetch();
          toast({ title: "Property added successfully" });
        }}
      />

      {/* MVP2-282: Import CSV Modal */}
      <PropertyImportModal
        open={showImportModal}
        onOpenChange={setShowImportModal}
        projectId={clientId!}
        onComplete={() => {
          refetch();
          toast({ title: "Properties imported successfully" });
        }}
      />
      </div>
    </div>
  );
}
